import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { customerEndpoints } from '../../services/api/endpoints/customer.endpoint';
import { Customer } from '../../types/customer';
import { logger } from '../../utils/logger';

export interface PaginatedCustomerData {
  customers: Customer[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useCustomers = (
  page = 1,
  pageSize = 10,
  sortBy = 'createdAt',
  sortOrder: 'ASC' | 'DESC' = 'DESC',
  searchTerm?: string
): PaginatedCustomerData => {
  const [data, setData] = useState<{
    customers: Customer[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const fetchCustomers = useCallback(async () => {
    if (!selectedOrganizationId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await customerEndpoints.listCustomers(
        selectedOrganizationId,
        page,
        pageSize,
        sortBy,
        sortOrder,
        searchTerm
      );
      setData(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch customers';
      setError(message);
      logger.error('Error fetching customers:', err);
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrganizationId, page, pageSize, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  return {
    customers: data?.customers ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? page,
    pageSize: data?.pageSize ?? pageSize,
    totalPages: data?.totalPages ?? 0,
    isLoading,
    error,
    refetch: fetchCustomers,
  };
};
